import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { CollapsibleParticipants } from "@/components/artifacts/view/blocks/CollapsibleParticipants.tsx";
import { externalParticipantStyles } from "@/components/artifacts/view/blocks/participantStyles.ts";
import { useAssistantFlags } from "@/components/artifacts/view/useAssistantFlags.ts";
import { EmailFromPickerPopover } from "@/components/artifacts/EmailFromPickerPopover.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ContactInput } from "@/components/ui/contact-input.tsx";
import { ParticipantPill } from "@/components/ui/participant-pill.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { createContactWithHandle } from "@/hooks/useContacts.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent, ViewParticipant } from "@/types/artifacts-view.ts";
import type { Contact, Handle } from "@/types/contact.ts";
import { useState } from "react";

type EmailRecipientsComponentData = Extract<ArtifactComponent, { type: "emailRecipients" }>;

type RecipientField = "to" | "cc" | "bcc";

const EmailPill = ({
  participant,
  canDelete,
  onDelete,
  disableContactSidebar,
}: {
  participant: ViewParticipant;
  canDelete?: boolean;
  onDelete?: () => void;
  disableContactSidebar?: boolean;
}) => {
  // Assistant detection is shared with other participant blocks so the okthx
  // pill is consistently pinned (non-deletable) and labeled "Okthx".
  const { isAssistant, isOwnAssistant } = useAssistantFlags()(participant.value);
  const isExternal = !participant.isDomainHandle;

  return (
    <ParticipantPill
      participant={participant}
      variant="email"
      showAvatar={true}
      showParticipantStatus={false}
      isExternal={isExternal}
      className={cn(isExternal && externalParticipantStyles, "w-fit")}
      isAssistant={isAssistant}
      isOwnAssistant={isOwnAssistant}
      canDelete={canDelete}
      onDelete={onDelete}
      disableContactSidebar={disableContactSidebar}
    />
  );
};

const RecipientRow = ({
  label,
  participants,
  editing,
  onAdd,
  onRemove,
}: {
  label: string;
  participants: ViewParticipant[];
  editing: boolean;
  onAdd: (contact: Contact, handle?: Handle) => Promise<void>;
  onRemove: (id: string) => void;
}) => {
  if (!editing && participants.length === 0) return null;
  return (
    <div className="flex flex-row items-start gap-2 text-sm">
      <div className="font-medium w-20 shrink-0 pt-0.5">{label}</div>
      <div className="flex flex-row flex-wrap gap-1 items-center flex-1">
        {participants.map((p) => (
          <EmailPill
            key={p.id}
            participant={p}
            canDelete={editing}
            onDelete={editing ? () => onRemove(p.id) : undefined}
          />
        ))}
        {editing ? (
          <ContactInput
            onSelectContact={(contact) => onAdd(contact)}
            onSelectHandle={(contact, handle) => onAdd(contact, handle)}
            onSelectEmail={async (email) => {
              try {
                const contact = await createContactWithHandle({
                  contactName: "",
                  handleType: "emailAddress",
                  handleValue: email,
                });
                await onAdd(contact);
              } catch (error) {
                console.error(error);
                errorToast("Error adding recipient");
              }
            }}
            buttonClassName="h-6 w-6"
            statsigName={`artifact-email-add-${label.toLowerCase()}`}
          />
        ) : null}
      </div>
    </div>
  );
};

export const EmailRecipientsBlock = ({
  component,
}: {
  component: EmailRecipientsComponentData;
}) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const [editing, setEditing] = useState(false);

  const to = component.to;
  const cc = component.cc ?? [];
  const bcc = component.bcc ?? [];

  const writeIds = async (field: RecipientField, next: ViewParticipant[]) => {
    const path =
      field === "to"
        ? component.toEditPath
        : field === "cc"
          ? component.ccEditPath
          : component.bccEditPath;
    try {
      await patchOne(
        path,
        next.map((p) => p.id),
      );
    } catch (error) {
      console.error(error);
      errorToast("Error updating recipients");
    }
  };

  const addTo = (field: RecipientField) => async (contact: Contact, selectedHandle?: Handle) => {
    const handle = selectedHandle ?? contact.handles?.find((h) => h.type === "emailAddress");
    if (!handle) {
      errorToast("Contact has no email address");
      return;
    }
    const current = field === "to" ? to : field === "cc" ? cc : bcc;
    if (current.some((p) => p.id === handle.id)) {
      errorToast(`${handle.value} is already a ${field.toUpperCase()} recipient`);
      return;
    }
    const next: ViewParticipant[] = [
      ...current,
      {
        id: handle.id,
        urn: handle.urn,
        name: contact.name,
        picture: contact.picture,
        value: handle.value,
        type: handle.type as "emailAddress" | "phoneNumber" | "address",
        contactId: contact.id,
      },
    ];
    await writeIds(field, next);
  };

  const removeFrom = (field: RecipientField) => (participantId: string) => {
    const current = field === "to" ? to : field === "cc" ? cc : bcc;
    const next = current.filter((p) => p.id !== participantId);
    void writeIds(field, next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2 text-sm">
          <div className="font-medium w-20 shrink-0">From:</div>
          {canEdit && component.fromEditPath ? (
            <EmailFromPickerPopover
              trigger={<EmailPill participant={component.from} disableContactSidebar />}
              selectedHandleId={component.from.id}
              onSelect={async (option) => {
                try {
                  await patchOne("fromEmail", option.handleValue);
                } catch (error) {
                  console.error(error);
                  errorToast("Error updating sender");
                }
              }}
            />
          ) : (
            <EmailPill participant={component.from} />
          )}
        </div>
        {canEdit ? (
          <Button
            onClick={() => setEditing(!editing)}
            variant="outlinePopover"
            size="action"
            className="h-6 text-xs px-2 py-1 font-medium"
            aria-label="Edit recipients"
            data-statsig-name="artifact-email-edit-recipients"
          >
            {editing ? "Done" : "Edit"}
          </Button>
        ) : null}
      </div>

      {editing ? (
        <>
          <RecipientRow
            label="To:"
            participants={to}
            editing
            onAdd={addTo("to")}
            onRemove={removeFrom("to")}
          />
          <RecipientRow
            label="Cc:"
            participants={cc}
            editing
            onAdd={addTo("cc")}
            onRemove={removeFrom("cc")}
          />
          <RecipientRow
            label="Bcc:"
            participants={bcc}
            editing
            onAdd={addTo("bcc")}
            onRemove={removeFrom("bcc")}
          />
        </>
      ) : (
        <div className="flex flex-row items-start gap-2 text-sm">
          <div className="font-medium w-20 shrink-0 pt-0.5">Recipients:</div>
          <CollapsibleParticipants
            participants={[...to, ...cc, ...bcc]}
            renderParticipant={(p) => <EmailPill key={p.id} participant={p} />}
            className="flex-1"
          />
        </div>
      )}
    </div>
  );
};
